import { defineNuxtRouteMiddleware, navigateTo } from '#app';

interface ClientUser {
  client_id: string;
  permissions: string[];
}

interface AuthUser {
  permissions?: string[];
  client_users?: ClientUser[];
}

/**
 * Permission-based route access middleware
 * Checks if user has required permissions defined in route meta auth_permissions
 */
export default defineNuxtRouteMiddleware(to => {
  if (import.meta.server) return;

  try {
    const auth = useAuth();

    if (!auth.loggedIn) {
      return navigateTo('/login');
    }

    const requiredPermissions = (to.meta.auth_permissions as string[]) || [];
    if (requiredPermissions.length === 0) return;

    const user = auth.user as AuthUser;
    const globalPermissions = (user?.permissions || []).map(p =>
      p.toLowerCase()
    );

    // Check global permissions
    const hasGlobalAccess = requiredPermissions.some(permission =>
      globalPermissions.includes(permission.toLowerCase())
    );

    if (hasGlobalAccess) return;

    // Check client-specific permissions
    if (user?.client_users?.length) {
      const clientStore = useClientsStore();
      const clientUser = clientStore.selectedClientId
        ? user.client_users.find(
            cu => cu.client_id === clientStore.selectedClientId
          )
        : user.client_users[0];

      if (clientUser?.permissions) {
        const clientPermissions = clientUser.permissions.map(p =>
          p.toLowerCase()
        );
        const hasClientAccess = requiredPermissions.some(permission =>
          clientPermissions.includes(permission.toLowerCase())
        );

        if (hasClientAccess) return;
      }
    }

    return navigateTo('/access-denied');
  } catch {
    return navigateTo('/login');
  }
});
